import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  LinkRequest,
  CurriculumInfo,
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout,
  googleLogin as apiGoogleLogin,
  appleLogin as apiAppleLogin,
  deleteAccount as apiDeleteAccount,
  syncUser,
  getMe,
  getStudentLinkRequests,
  getMyCurricula,
} from '../services/auth';
import { checkAdmin } from '../services/adminApi';
import { onAuthChange, FirebaseUser } from '../services/firebase';
import { storage } from '../services/storage';

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<{ has_multiple_curricula: boolean; available_curricula: CurriculumInfo[] }>;
  register: (email: string, password: string, name: string, role: 'student' | 'parent', curriculum?: string) => Promise<{ has_multiple_curricula: boolean; available_curricula: CurriculumInfo[] }>;
  logout: () => Promise<void>;
  googleLogin: (role?: 'student' | 'parent', curriculum?: string, allowCreate?: boolean) => Promise<{ has_multiple_curricula: boolean; available_curricula: CurriculumInfo[] }>;
  appleLogin: (role?: 'student' | 'parent', curriculum?: string, allowCreate?: boolean) => Promise<{ has_multiple_curricula: boolean; available_curricula: CurriculumInfo[] }>;
  refreshUser: () => Promise<void>;
  selectedChildId: string | null;
  setSelectedChildId: (id: string | null) => void;
  clearError: () => void;
  deleteAccount: () => Promise<void>;
  pendingLinkRequests: LinkRequest[];
  refreshLinkRequests: () => Promise<void>;
  // Multi-curriculum support
  availableCurricula: CurriculumInfo[];
  currentCurriculum: string | null;
  switchCurriculum: (curriculum: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(() => {
    return localStorage.getItem('selectedChildId');
  });
  const [pendingLinkRequests, setPendingLinkRequests] = useState<LinkRequest[]>([]);
  const [availableCurricula, setAvailableCurricula] = useState<CurriculumInfo[]>([]);
  const [currentCurriculum, setCurrentCurriculum] = useState<string | null>(() => {
    return storage.getCurriculum();
  });
  // Flag to prevent onAuthChange from overwriting state during login/register
  const [isAuthInProgress, setIsAuthInProgress] = useState(false);

  const refreshLinkRequests = async () => {
    try {
      const requests = await getStudentLinkRequests();
      setPendingLinkRequests(requests);
    } catch {
      setPendingLinkRequests([]);
    }
  };

  // Sync storage whenever user changes — must happen synchronously
  // before any page component reads storage.getStudentId()
  const syncStorage = (u: User | null, curriculum?: string | null) => {
    if (u) {
      if (u.role === 'student' && u.student_id) {
        storage.setStudentId(u.student_id);
        storage.setStudentName(u.name);
        if (curriculum !== undefined) {
          storage.setCurriculum(curriculum);
        }
      }
    } else {
      storage.clear();
    }
  };

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthChange(async (fbUser) => {
      setFirebaseUser(fbUser);

      // Skip if a login/register is in progress - those handlers will set state
      if (isAuthInProgress) {
        setLoading(false);
        return;
      }

      if (fbUser) {
        // User is signed in to Firebase, try to get our backend user
        try {
          const backendUser = await getMe();
          // Check admin status
          try {
            await checkAdmin();
            backendUser.isAdmin = true;
          } catch {
            backendUser.isAdmin = false;
          }
          syncStorage(backendUser);  // Sync BEFORE setting state
          setUser(backendUser);

          // Fetch curricula for students
          if (backendUser.role === 'student') {
            getMyCurricula()
              .then((curricula) => {
                setAvailableCurricula(curricula);
                // Set current curriculum if not already set
                if (!storage.getCurriculum() && backendUser.curriculum) {
                  storage.setCurriculum(backendUser.curriculum);
                  setCurrentCurriculum(backendUser.curriculum);
                }
              })
              .catch(() => setAvailableCurricula([]));

            // Fetch pending link requests for students
            getStudentLinkRequests()
              .then(setPendingLinkRequests)
              .catch(() => setPendingLinkRequests([]));
          }
        } catch {
          // User exists in Firebase but not synced to backend yet
          syncStorage(null);
          setUser(null);
        }
      } else {
        // User is signed out
        syncStorage(null);
        setUser(null);
        setAvailableCurricula([]);
        setCurrentCurriculum(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [isAuthInProgress]);

  // Persist selected child ID
  useEffect(() => {
    if (selectedChildId) {
      localStorage.setItem('selectedChildId', selectedChildId);
    } else {
      localStorage.removeItem('selectedChildId');
    }
  }, [selectedChildId]);

  const login = async (email: string, password: string) => {
    setIsAuthInProgress(true);
    try {
      setError(null);
      const response = await apiLogin(email, password);
      syncStorage(response.user, response.user.curriculum);
      setUser(response.user);
      setAvailableCurricula(response.available_curricula || []);
      if (response.user.curriculum) {
        setCurrentCurriculum(response.user.curriculum);
      }
      setLoading(false);
      return {
        has_multiple_curricula: response.has_multiple_curricula,
        available_curricula: response.available_curricula || [],
      };
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || 'Login failed';
      setError(message);
      throw new Error(message);
    } finally {
      setIsAuthInProgress(false);
    }
  };

  const register = async (email: string, password: string, name: string, role: 'student' | 'parent', curriculum?: string) => {
    setIsAuthInProgress(true);
    try {
      setError(null);
      const response = await apiRegister(email, password, name, role, curriculum);
      syncStorage(response.user, response.user.curriculum);
      setUser(response.user);
      setAvailableCurricula(response.available_curricula || []);
      if (response.user.curriculum) {
        setCurrentCurriculum(response.user.curriculum);
      }
      setLoading(false);
      return {
        has_multiple_curricula: response.has_multiple_curricula,
        available_curricula: response.available_curricula || [],
      };
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || 'Registration failed';
      setError(message);
      throw new Error(message);
    } finally {
      setIsAuthInProgress(false);
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
    } finally {
      syncStorage(null);
      setUser(null);
      setSelectedChildId(null);
      setAvailableCurricula([]);
      setCurrentCurriculum(null);
    }
  };

  const googleLogin = async (role: 'student' | 'parent' = 'student', curriculum?: string, allowCreate: boolean = true) => {
    setIsAuthInProgress(true);
    try {
      setError(null);
      const response = await apiGoogleLogin(role, curriculum, allowCreate);
      syncStorage(response.user, response.user.curriculum);
      setUser(response.user);
      setAvailableCurricula(response.available_curricula || []);
      if (response.user.curriculum) {
        setCurrentCurriculum(response.user.curriculum);
      }
      setLoading(false);
      return {
        has_multiple_curricula: response.has_multiple_curricula,
        available_curricula: response.available_curricula || [],
      };
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || 'Google login failed';
      setError(message);
      throw new Error(message);
    } finally {
      setIsAuthInProgress(false);
    }
  };

  const appleLogin = async (role: 'student' | 'parent' = 'student', curriculum?: string, allowCreate: boolean = true) => {
    setIsAuthInProgress(true);
    try {
      setError(null);
      const response = await apiAppleLogin(role, curriculum, allowCreate);
      syncStorage(response.user, response.user.curriculum);
      setUser(response.user);
      setAvailableCurricula(response.available_curricula || []);
      if (response.user.curriculum) {
        setCurrentCurriculum(response.user.curriculum);
      }
      setLoading(false);
      return {
        has_multiple_curricula: response.has_multiple_curricula,
        available_curricula: response.available_curricula || [],
      };
    } catch (err: any) {
      const message = err.response?.data?.detail || err.message || 'Apple login failed';
      setError(message);
      throw new Error(message);
    } finally {
      setIsAuthInProgress(false);
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await getMe();
      setUser(userData);

      // Also refresh curricula for students
      if (userData.role === 'student') {
        try {
          const curricula = await getMyCurricula();
          setAvailableCurricula(curricula);
        } catch {
          // Keep existing curricula on error
        }
      }
    } catch {
      // If we have a Firebase user but no backend user, try to sync
      if (firebaseUser) {
        try {
          const response = await syncUser();
          setUser(response.user);
          setAvailableCurricula(response.available_curricula || []);
        } catch {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    }
  };

  const clearError = () => setError(null);

  const deleteAccount = async () => {
    await apiDeleteAccount();
    syncStorage(null);
    setUser(null);
    setAvailableCurricula([]);
    setCurrentCurriculum(null);
  };

  const switchCurriculum = async (curriculum: string) => {
    // Find the curriculum info to get the student_id
    const curriculumInfo = availableCurricula.find(c => c.curriculum === curriculum);
    if (!curriculumInfo) {
      throw new Error('Curriculum not found');
    }

    // Update storage first
    storage.setCurriculum(curriculum);
    storage.setStudentId(curriculumInfo.student_id);
    setCurrentCurriculum(curriculum);

    // Re-fetch user with new curriculum header
    try {
      const backendUser = await getMe();
      setUser(backendUser);

      // Refresh link requests for the new curriculum's student_id
      if (backendUser.role === 'student') {
        getStudentLinkRequests()
          .then(setPendingLinkRequests)
          .catch(() => setPendingLinkRequests([]));
      }
    } catch (err) {
      // Revert on error
      const previousCurriculum = user?.curriculum;
      if (previousCurriculum) {
        storage.setCurriculum(previousCurriculum);
        setCurrentCurriculum(previousCurriculum);
      }
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        firebaseUser,
        loading,
        error,
        login,
        register,
        logout,
        googleLogin,
        appleLogin,
        refreshUser,
        selectedChildId,
        setSelectedChildId,
        clearError,
        deleteAccount,
        pendingLinkRequests,
        refreshLinkRequests,
        availableCurricula,
        currentCurriculum,
        switchCurriculum,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
